/**
 * 「我们真正发给视频模型的那条 prompt」实时预览(2026-08-06)。
 *
 * ═══ 为什么要防抖 + 取消 ═══════════════════════════════════════════════════════
 * 用户在分镜卡里逐字改环境/动作,每敲一个键都发一次请求既浪费也乱序 —— 后发的先回,
 * 右栏就会显示**上一次**的 prompt(比不显示更糟:用户以为自己刚才那下没生效)。
 * 所以:800ms 防抖 + 只认最后一次(用序号丢弃过期回包,不靠 AbortController ——
 * 请求本身很便宜,要防的是**乱序渲染**,不是省那一次网络)。
 *
 * ★零分镜时不发请求:没有镜就没有段,ai 会如实返空数组,但那一趟纯属白跑。
 * ★失败不清空上一次结果:网络抖一下就把右栏整块变白,比留着上一版(标一句"刷新失败")糟。
 */
import { useEffect, useRef, useState } from "react";

import { videoService, type VideoPromptSegmentDto } from "../api/videoService";
import { buildTaskRequest, type BuildTaskRequestInput } from "./buildTaskRequest";
import { previewFingerprint } from "./previewFingerprint";
import { errorText } from "@/shared/lib/apiErrors";

const DEBOUNCE_MS = 800;

export interface CompilePreviewState {
  segments: VideoPromptSegmentDto[];
  charBudget: number;
  loading: boolean;
  /** 上一次刷新失败(仍显示旧结果,如实标一句)。 */
  staleError: string | null;
}

export function useCompilePreview(
  input: BuildTaskRequestInput,
  enabled: boolean
): CompilePreviewState {
  const [state, setState] = useState<CompilePreviewState>({
    segments: [],
    charBudget: 1000, // VID-RICH-1:与 ai max_prompt_chars 基线同步(老 ai 不回 promptCharBudget 时的兜底)
    loading: false,
    staleError: null,
  });
  /** 只认最后一次回包(防乱序渲染,见头注)。 */
  const seqRef = useRef(0);

  // 组包本身是纯函数 —— 拿它的指纹当依赖键,form 里与 prompt 无关的字段
  // (草稿 id、任务名…)变了也不会白刷一次。
  //
  // ★2026-08-19 修:这里原来是 `JSON.stringify(req)`,**把签名 URL 也算进去了**。
  //   `pollFrameGen` 每 2500ms 重签一次全部帧、签名每次都不同 → 这个键每轮都变 →
  //   编排/出图期间 compile-preview **每 2.5 秒白跑一次**(backend 一次 + ai 一次),
  //   而右栏那条 prompt 一个字都不会变。backend 日志实测:presign ↔ compile-preview
  //   每 1.7 秒交替、连打 40 秒。改用 previewFingerprint(把 URL 归一成「有/无」,
  //   见那边头注:整个剔掉会丢掉「首帧从无到有要重编」)。
  const req = enabled ? buildTaskRequest(input) : null;
  const key = req ? previewFingerprint(req) : "";

  useEffect(() => {
    if (!enabled || !req || !(req.panelScenes && req.panelScenes.length > 0)) {
      setState((s) => ({ ...s, segments: [], loading: false, staleError: null }));
      return;
    }
    const mine = ++seqRef.current;
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      videoService
        .compilePreview(req)
        .then((r) => {
          if (mine !== seqRef.current) return; // 过期回包,丢
          setState({
            segments: r.segments ?? [],
            charBudget: r.promptCharBudget ?? 1000,
            loading: false,
            staleError: null,
          });
        })
        .catch((e: unknown) => {
          if (mine !== seqRef.current) return;
          // 不清 segments:留着上一版比整块变白好(见头注)
          setState((s) => ({
            ...s,
            loading: false,
            staleError: errorText(e),
          }));
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
    // key 就是请求体本身的指纹 —— 它变了才需要重算
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return state;
}

/** 某一镜落在哪一段(右栏「这一镜进的是第 N 段」用);找不到返 -1。 */
export function segmentIndexOfShot(segments: VideoPromptSegmentDto[], shot: number): number {
  return segments.findIndex((s) => s.shotIndexes.includes(shot));
}
